import type {
  CreateWholesaleOrderInput,
  WholesaleCustomerView,
  WholesaleDuplicateCandidate,
  WholesaleOrderHeaderPatch,
} from "../../../shared/contracts.js";
import { LIMITS } from "../../../shared/contracts.js";
import { normalizeName } from "../../../shared/formatting.js";
import { TextField, FormField } from "../../ui/FormField.js";
import type { DuplicateLink } from "../cases/order-form/DuplicateWarningDialog.js";
import {
  isBlankLine,
  isCalendarDate,
  LINE_MESSAGES,
  lineFieldKey,
  newLineValues,
  toPartLineInput,
  validateLine,
  type OrderFormLineErrors,
  type OrderFormLineValues,
} from "../cases/order-form/PartLinesGrid.js";
import { wholesaleOrderPath } from "./presentation.js";

/**
 * The wholesale order form (spec §4.3): its editing type, the validation
 * that runs in the browser before the server's own, the mapping to the
 * shared `CreateWholesaleOrderInput` and the header patch, and the header
 * fields the new-order page and the edit sheet both render. The parts grid
 * is the one both sides share (`PartLinesGrid.tsx`).
 *
 * Everything here is text as Parts typed it, normalized only at the
 * boundary; the server validates again. The customer is the one required
 * header field — picked from the directory, never typed — the reference,
 * R&R number (attachable later), master ETA and note are optional.
 */

// ---------------------------------------------------------------------------
// Editing types
// ---------------------------------------------------------------------------

export type WholesaleFormValues = {
  /** The chosen customer, or `null` until one is picked. */
  customer: WholesaleCustomerView | null;
  customerReference: string;
  rrNumber: string;
  /** "ETA for all parts" as typed (`YYYY-MM-DD` or blank). */
  masterEta: string;
  orderNote: string;
  partLines: OrderFormLineValues[];
};

export type WholesaleHeaderValues = Omit<WholesaleFormValues, "partLines">;
export type WholesaleFieldKey = keyof WholesaleHeaderValues;

export type WholesaleFormErrors = Readonly<{
  fields: Partial<Record<WholesaleFieldKey, string>>;
  /** Keyed by the line's `clientId`. */
  lines: Readonly<Record<string, OrderFormLineErrors>>;
  isValid: boolean;
}>;

export const NO_WHOLESALE_ERRORS: WholesaleFormErrors = { fields: {}, lines: {}, isValid: true };

/** The server's field names for the header, mapped onto the form's. */
const SERVER_FIELD_KEYS: Readonly<Record<string, WholesaleFieldKey>> = {
  customerId: "customer",
  customerReference: "customerReference",
  rrNumber: "rrNumber",
  masterEta: "masterEta",
  orderNote: "orderNote",
};

export function emptyWholesaleForm(): WholesaleFormValues {
  return { customer: null, customerReference: "", rrNumber: "", masterEta: "", orderNote: "", partLines: [newLineValues()] };
}

/** True while nothing has been typed or chosen — the state a fresh form starts in. */
export function isPristineWholesaleForm(values: WholesaleFormValues): boolean {
  const { partLines, customer, ...header } = values;
  return (
    customer === null &&
    Object.values(header).every((value) => value === "") &&
    partLines.length === 1 &&
    isBlankLine(partLines[0]!)
  );
}

// ---------------------------------------------------------------------------
// Messages and validation
// ---------------------------------------------------------------------------

export const WHOLESALE_FORM_MESSAGES = {
  customer: "Choose the customer.",
  reference: `Use up to ${LIMITS.wholesaleReference.max} characters.`,
  orderNote: `Use up to ${LIMITS.note.max} characters.`,
  masterEta: LINE_MESSAGES.masterEta,
} as const;

export function validateWholesaleHeader(values: WholesaleHeaderValues): Partial<Record<WholesaleFieldKey, string>> {
  const fields: Partial<Record<WholesaleFieldKey, string>> = {};
  if (values.customer === null) fields.customer = WHOLESALE_FORM_MESSAGES.customer;
  if (normalizeName(values.customerReference).length > LIMITS.wholesaleReference.max) {
    fields.customerReference = WHOLESALE_FORM_MESSAGES.reference;
  }
  if (normalizeName(values.rrNumber).length > LIMITS.wholesaleReference.max) fields.rrNumber = WHOLESALE_FORM_MESSAGES.reference;
  if (values.masterEta.trim() !== "" && !isCalendarDate(values.masterEta.trim())) {
    fields.masterEta = WHOLESALE_FORM_MESSAGES.masterEta;
  }
  if (values.orderNote.trim().length > LIMITS.note.max) fields.orderNote = WHOLESALE_FORM_MESSAGES.orderNote;
  return fields;
}

export function validateWholesaleForm(values: WholesaleFormValues): WholesaleFormErrors {
  const fields = validateWholesaleHeader(values);
  const lines: Record<string, OrderFormLineErrors> = {};
  for (const line of values.partLines) {
    const errors = validateLine(line);
    if (Object.keys(errors).length > 0) lines[line.clientId] = errors;
  }
  return { fields, lines, isValid: Object.keys(fields).length === 0 && Object.keys(lines).length === 0 };
}

/**
 * Maps a server 422's `fieldErrors` (`customerId`, `partLines.0.eta`,
 * `patch.rrNumber`) onto the form; paths the form has no field for are
 * returned so the caller can show them as a general message.
 */
export function wholesaleErrorsFromServer(
  fieldErrors: Readonly<Record<string, readonly string[]>>,
  values: WholesaleFormValues,
): { errors: WholesaleFormErrors; unmapped: string[] } {
  const fields: Partial<Record<WholesaleFieldKey, string>> = {};
  const lines: Record<string, OrderFormLineErrors> = {};
  const unmapped: string[] = [];
  for (const [path, messages] of Object.entries(fieldErrors)) {
    const message = messages.find((text) => text.length > 0) ?? "Check this field.";
    const key = path.startsWith("patch.") ? path.slice("patch.".length) : path;
    const headerKey = SERVER_FIELD_KEYS[key];
    if (headerKey) {
      fields[headerKey] = message;
      continue;
    }
    if (path.startsWith("partLines.")) {
      const [indexText, field] = path.slice("partLines.".length).split(".");
      const index = Number(indexText);
      const line = Number.isInteger(index) ? values.partLines[index] : undefined;
      const lineKey = lineFieldKey(field);
      if (line && lineKey) {
        lines[line.clientId] = { ...lines[line.clientId], [lineKey]: message };
        continue;
      }
    }
    unmapped.push(message);
  }
  return {
    errors: { fields, lines, isValid: Object.keys(fields).length === 0 && Object.keys(lines).length === 0 },
    unmapped,
  };
}

// ---------------------------------------------------------------------------
// Mapping to the shared contract
// ---------------------------------------------------------------------------

function blankToUndefined(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

function trimmedOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/** `POST /api/wholesale/orders` from the form: blank optional fields are left out; the override only after a warning. */
export function toCreateWholesaleOrderInput(
  values: WholesaleFormValues,
  options: { duplicateOverrideReason?: string } = {},
): CreateWholesaleOrderInput {
  const customerReference = blankToUndefined(values.customerReference);
  const rrNumber = blankToUndefined(values.rrNumber);
  const masterEta = blankToUndefined(values.masterEta);
  const orderNote = blankToUndefined(values.orderNote);
  const duplicateOverrideReason = blankToUndefined(options.duplicateOverrideReason ?? "");
  return {
    customerId: values.customer?.id ?? "",
    ...(customerReference !== undefined ? { customerReference } : {}),
    ...(rrNumber !== undefined ? { rrNumber } : {}),
    ...(masterEta !== undefined ? { masterEta } : {}),
    ...(orderNote !== undefined ? { orderNote } : {}),
    ...(duplicateOverrideReason !== undefined ? { duplicateOverrideReason } : {}),
    partLines: values.partLines.map(toPartLineInput),
  };
}

/** The header fields whose value differs from the order on record; a cleared field travels as `null`. */
export function wholesaleHeaderPatch(baseline: WholesaleHeaderValues, values: WholesaleHeaderValues): WholesaleOrderHeaderPatch {
  const patch: WholesaleOrderHeaderPatch = {};
  if (values.customer && values.customer.id !== baseline.customer?.id) patch.customerId = values.customer.id;
  const reference = trimmedOrNull(normalizeName(values.customerReference));
  if (reference !== trimmedOrNull(normalizeName(baseline.customerReference))) patch.customerReference = reference;
  const rrNumber = trimmedOrNull(normalizeName(values.rrNumber));
  if (rrNumber !== trimmedOrNull(normalizeName(baseline.rrNumber))) patch.rrNumber = rrNumber;
  const masterEta = trimmedOrNull(values.masterEta);
  if (masterEta !== trimmedOrNull(baseline.masterEta)) patch.masterEta = masterEta;
  const note = trimmedOrNull(values.orderNote);
  if (note !== trimmedOrNull(baseline.orderNote)) patch.orderNote = note;
  return patch;
}

/** The matching orders a 409 `DUPLICATE_WHOLESALE_ORDER` names, as the warning dialog lists them. */
export function wholesaleDuplicateLinks(candidates: readonly WholesaleDuplicateCandidate[]): DuplicateLink[] {
  return candidates.map((candidate) => ({
    key: candidate.orderId,
    number: candidate.number,
    path: wholesaleOrderPath(candidate.orderId),
    reason: "Same customer and reference within 30 days",
  }));
}

// ---------------------------------------------------------------------------
// Header fields (shared by the new-order page and the edit sheet)
// ---------------------------------------------------------------------------

export interface WholesaleDetailsFieldsProps {
  values: Pick<WholesaleHeaderValues, "customerReference" | "rrNumber" | "orderNote">;
  errors: Partial<Record<WholesaleFieldKey, string>>;
  onChange: (patch: Partial<WholesaleHeaderValues>) => void;
  disabled?: boolean;
  /** Prefix for control ids, so two forms on one page never collide. */
  idPrefix: string;
}

/** The reference, the R&R number and the note. */
export function WholesaleDetailsFields({ values, errors, onChange, disabled, idPrefix }: WholesaleDetailsFieldsProps) {
  const id = (name: string) => `${idPrefix}-${name}`;
  return (
    <div className="order-form__grid">
      <TextField
        id={id("customerReference")}
        label="Customer reference"
        hint="The customer's PO or reference. Used for the duplicate check."
        optional="untagged"
        error={errors.customerReference ?? null}
        value={values.customerReference}
        onChange={(event) => onChange({ customerReference: event.target.value })}
        autoComplete="off"
        spellCheck={false}
        maxLength={LIMITS.wholesaleReference.max + 10}
        disabled={disabled}
      />
      <TextField
        id={id("rrNumber")}
        label="R&R number"
        hint="The Reynolds & Reynolds invoice or order number. Can be attached later."
        optional="untagged"
        error={errors.rrNumber ?? null}
        value={values.rrNumber}
        onChange={(event) => onChange({ rrNumber: event.target.value })}
        autoComplete="off"
        spellCheck={false}
        maxLength={LIMITS.wholesaleReference.max + 10}
        disabled={disabled}
      />
      <FormField id={id("orderNote")} label="Order note" optional error={errors.orderNote ?? null} className="order-form__field--wide">
        {(control) => (
          <textarea
            {...control}
            className="text-input textarea-input"
            data-size="desktop"
            rows={3}
            value={values.orderNote}
            onChange={(event) => onChange({ orderNote: event.target.value })}
            maxLength={LIMITS.note.max + 10}
            disabled={disabled}
          />
        )}
      </FormField>
    </div>
  );
}
